import type { EconHeaderCountdownEvent } from "../../../types/econ-header"
import {
  getEconHeaderCountdownCandidate,
  hasEconHeaderActual,
} from "../../../lib/econ-header-countdown"

export interface PerformanceEconEventModel {
  displayEvent: EconHeaderCountdownEvent | null
  latestReleasedEvent: EconHeaderCountdownEvent | null
  nextEvent: EconHeaderCountdownEvent | null
}

export function buildPerformanceEconEventModel(
  events: EconHeaderCountdownEvent[],
  now = Date.now(),
): PerformanceEconEventModel {
  const deskEvents = events.filter((event) => event.source === "desk-plan")
  const activeEvent = getEconHeaderCountdownCandidate(deskEvents, now)
  const latestReleasedEvent = findLatestReleasedEvent(deskEvents, now)
  const nextEvent = findNextEvent(deskEvents, now)

  return {
    displayEvent: activeEvent ?? latestReleasedEvent ?? nextEvent,
    latestReleasedEvent,
    nextEvent,
  }
}

function findLatestReleasedEvent(
  events: EconHeaderCountdownEvent[],
  now: number,
): EconHeaderCountdownEvent | null {
  return (
    events
      .filter(
        (event) =>
          event.status === "released" &&
          hasEconHeaderActual(event.actualValue) &&
          isSameLocalDay(releasedAt(event), now),
      )
      .sort((left, right) => releasedAt(right) - releasedAt(left))[0] ?? null
  )
}

function findNextEvent(
  events: EconHeaderCountdownEvent[],
  now: number,
): EconHeaderCountdownEvent | null {
  return (
    events
      .filter((event) => new Date(event.scheduledAt).getTime() > now)
      .sort(
        (left, right) =>
          new Date(left.scheduledAt).getTime() -
          new Date(right.scheduledAt).getTime(),
      )[0] ?? null
  )
}

function releasedAt(event: EconHeaderCountdownEvent): number {
  const released = new Date(
    event.actualPrintedAt ?? event.scheduledAt,
  ).getTime()
  return Number.isFinite(released) ? released : 0
}

function isSameLocalDay(timestamp: number, now: number): boolean {
  if (!Number.isFinite(timestamp)) return false
  return new Date(timestamp).toDateString() === new Date(now).toDateString()
}
